import React from 'react';
import {
  CardContent,
  CardActionArea,
  Typography,
  Tooltip,
  Grid,
} from '@mui/material';
import { LottieIcon, LottieName } from './patterns';
import { MotionCard } from './motion';
import { MOBILE_DENSITY, phoneMedia } from '../theme/mobileDensity';
import RaTrainingTile from './RaTrainingTile';

export type WorkflowType =
  | 'market_research'
  | 'clause_logic'
  | 'sow_verification'
  | 'construction_sow_generation'
  | 'services_pws_generation'
  | 'wage_determination'
  | 'pnm'
  | 'fopr'
  | 'services'
  | 'one_time_construction'
  | 'services_combo'
  | 'gpc'
  | 'complex_services'
  | 'other_transactions'
  | 'post_award_actions'
  | 'dcma_pbp'
  | 'dcma_crr';

interface WorkflowTypeGridProps {
  canAccessRaTraining?: boolean;
  onOpenRaTraining?: () => void;
  onSelect: (type: WorkflowType) => void;
  allowedWorkflowTypes?: string[] | null;
  allowDcmaPbp?: boolean;
  allowDcmaCrr?: boolean;
  /** The New Workflow popup keeps the DCMA pair at the end. */
  dcmaPairAtEnd?: boolean;
  /** Server-projected limited MR presentation for Customer callers. */
  marketResearchCustomerView?: boolean;
}

interface WorkflowCardProps {
  desktopColumns: 3 | 4;
  type: WorkflowType;
  title: string;
  stageCount: string;
  description: string;
  lottie: LottieName;
  onSelect: (type: WorkflowType) => void;
  /** Position in the grid — drives a gentle on-load animation stagger. */
  index: number;
}

const WorkflowCard: React.FC<WorkflowCardProps> = ({
  type,
  title,
  description,
  lottie,
  onSelect,
  index,
  desktopColumns,
}) => {
  // Hover drives the Lottie playback: the whole card is the hover target, so
  // hovering anywhere on the card animates its icon.
  const [hovered, setHovered] = React.useState(false);
  return (
    // The RA landing fits training and three workflows in one desktop row.
    // Other views retain their wider three-per-row desktop cards.
    <Grid item xs={12} sm={6} md={desktopColumns} xl={3}>
      <MotionCard
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        sx={{
          height: '100%',
          // MotionCard owns the spring scale; we animate border + shadow here
          // (NOT transform) for the signature breathable lift on hover.
          transition: 'border-color 0.18s ease, box-shadow 0.18s ease',
          '&:hover': {
            borderColor: 'primary.main',
            boxShadow: 3,
          },
          '&:focus-within': {
            borderColor: 'primary.main',
          },
        }}
      >
        {/* The card face shows only the animated icon + title; the description
            surfaces on hover/focus as a tooltip (mirroring the category cards). */}
        <Tooltip title={description} arrow>
          <CardActionArea
            onClick={() => onSelect(type)}
            onFocus={() => setHovered(true)}
            onBlur={() => setHovered(false)}
            sx={{
              height: '100%',
              px: { xs: MOBILE_DENSITY.cardInset, sm: 2 },
              py: 1.5,
              minHeight: MOBILE_DENSITY.touchTarget,
            }}
          >
            <CardContent
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                gap: 1,
                px: 1,
                py: 0,
                minHeight: 112,
                '&:last-child': { pb: 0 },
              }}
            >
              {/* Compact animated icon (vertical layout, mirroring the category
                  cards) — the LABEL dominates. No tinted tile; the animation
                  carries the color. Plays once on load (staggered) and on hover. */}
              <LottieIcon
                name={lottie}
                size={60}
                playing={hovered}
                animateOnLoad
                loadDelayMs={index * 90}
              />
              <Typography
                variant="h4"
                component="div"
                sx={{ lineHeight: 1.2, width: '100%' }}
              >
                {title}
              </Typography>
            </CardContent>
          </CardActionArea>
        </Tooltip>
      </MotionCard>
    </Grid>
  );
};

interface WorkflowDefinition {
  type: WorkflowType;
  title: string;
  stageCount: string;
  description: string;
  /** Animated Lottie icon name (resolves to `/lottie/<File>.lottie`). */
  lottie: LottieName;
}

const WORKFLOW_DEFINITIONS: WorkflowDefinition[] = [
  { type: 'dcma_crr', title: 'Contract Receipt and Review', stageCount: 'Contract checklist', description: 'Fill the original CR&R checklist from contract evidence', lottie: 'checklist' },
  {
    type: 'market_research',
    title: 'Market Research',
    stageCount: '12 stages',
    description: 'Generate synopsis, discover vendors, assign codes',
    lottie: 'delivery',
  },
  {
    type: 'clause_logic',
    title: 'Clause Logic',
    stageCount: '3 stages',
    description: 'Correct clauses, generate compliant XML clause file',
    lottie: 'scale',
  },
  {
    type: 'sow_verification',
    title: 'Construction SOW Verification',
    stageCount: '3 stages',
    description: 'Annotate Statement of Work, suggest revisions, edit corrected draft',
    lottie: 'scan_document',
  },
  {
    type: 'wage_determination',
    title: 'Wage Determination',
    stageCount: '3 stages',
    description: 'Find applicable Davis-Bacon wage rates for construction by location and construction type',
    lottie: 'money',
  },
  {
    type: 'pnm',
    title: 'Price Negotiation Memo (PNM)',
    stageCount: '2 stages',
    description: 'Analyze Independent Government Estimate vs quote discrepancies, generate questions for the Civil Engineering team and vendor',
    lottie: 'handshake',
  },
  {
    type: 'fopr',
    title: 'FOPR Generation',
    stageCount: '3 stages',
    description: 'Generate Fair Opportunity Proposal Requests and construction abstracts',
    lottie: 'notebook',
  },
  {
    type: 'services',
    title: 'One-time Services',
    stageCount: '7 stages',
    description: 'Review Performance Work Statement, assign codes, discover vendors',
    lottie: 'meeting',
  },
  {
    type: 'other_transactions',
    title: 'Other Transactions',
    stageCount: '8 stages',
    description: 'Prepare a Prototype OT award package from the need through proposed agreement and remaining actual-award actions',
    lottie: 'handshake',
  },
  {
    type: 'post_award_actions',
    title: 'Post-award Actions',
    stageCount: 'Draft-only support',
    description: 'Draft-only support for post-award changes, option planning, and recompete preparation',
    lottie: 'meeting',
  },
  {
    type: 'one_time_construction',
    title: 'One-time Construction',
    stageCount: '6 stages',
    description: 'Generate a new construction SOW from requirements, or verify an existing SOW, then assign codes and discover vendors',
    lottie: 'forklift',
  },
  {
    type: 'complex_services',
    title: 'Complex Services',
    stageCount: 'up to 11 stages',
    description: 'Full pre-award package (IGE, PWS, DD 2579, QASP), compliance checks, and tracker hand-off for a complex or recurring service acquisition',
    lottie: 'complex',
  },
  {
    type: 'services_combo',
    title: 'Complex Services Synopsis',
    stageCount: '2 stages',
    description: 'Auto-fill the Combined Synopsis/Solicitation and export a fillable PDF',
    lottie: 'notebook',
  },
  {
    type: 'gpc',
    title: 'GPC',
    stageCount: '3–11 stages',
    description: 'Government Purchase Card: clarify the buy, define salient characteristics, and decide the mandatory source',
    lottie: 'creditcard',
  },
  {
    type: 'dcma_pbp',
    title: 'DCMA PBP Review',
    stageCount: '3 stages',
    description: 'Verify a performance-based payment milestone schedule',
    lottie: 'checklist',
  },
];

const WorkflowTypeGrid: React.FC<WorkflowTypeGridProps> = ({
  onSelect,
  canAccessRaTraining = false,
  onOpenRaTraining,
  allowedWorkflowTypes,
  allowDcmaPbp = false,
  allowDcmaCrr = false,
  dcmaPairAtEnd = false,
  marketResearchCustomerView = false,
}) => {
  const entitledDefinitions = WORKFLOW_DEFINITIONS.filter(
    (definition) => (definition.type !== 'dcma_pbp' || allowDcmaPbp) && (definition.type !== 'dcma_crr' || allowDcmaCrr),
  );
  const permittedDefinitions = allowedWorkflowTypes
    ? allowedWorkflowTypes
        .map((v) => entitledDefinitions.find((d) => d.type === v))
        .filter((d): d is WorkflowDefinition => Boolean(d))
    : entitledDefinitions;
  // RA users receive GPC before Market Research from the entitlement service.
  // Keep their Customer homepage intuitive by placing the related MR card
  // immediately before GPC, without changing the card order for other roles.
  const customerOrderedDefinitions = marketResearchCustomerView
    ? (() => {
        const marketResearch = permittedDefinitions.find(
          (definition) => definition.type === 'market_research',
        );
        const withoutMarketResearch = permittedDefinitions.filter(
          (definition) => definition.type !== 'market_research',
        );
        const gpcIndex = withoutMarketResearch.findIndex(
          (definition) => definition.type === 'gpc',
        );

        if (!marketResearch || gpcIndex === -1) return permittedDefinitions;
        return [
          ...withoutMarketResearch.slice(0, gpcIndex),
          marketResearch,
          ...withoutMarketResearch.slice(gpcIndex),
        ];
      })()
    : permittedDefinitions;
  const visibleDefinitions: WorkflowDefinition[] = customerOrderedDefinitions.map((definition) => (
      definition.type === 'market_research' && marketResearchCustomerView
        ? {
            ...definition,
            title: 'Commodities Market Research',
            stageCount: '3 stages + Brand-name J&A when required',
            description: 'Clarify requirements, define salient characteristics, and discover products before CO/CS contracting work.',
          }
        : definition
    ));
  const showRaTraining = canAccessRaTraining && !!onOpenRaTraining;
  const desktopColumns = showRaTraining ? 3 : 4;

  const orderedDefinitions = dcmaPairAtEnd && visibleDefinitions.some(definition => definition.type === 'dcma_crr')
    ? [
        ...visibleDefinitions.filter(definition => definition.type !== 'dcma_pbp' && definition.type !== 'dcma_crr'),
        ...visibleDefinitions.filter(definition => definition.type === 'dcma_pbp'),
        ...visibleDefinitions.filter(definition => definition.type === 'dcma_crr'),
      ]
    : visibleDefinitions;

  return (
    <Grid
      container
      spacing={{ xs: MOBILE_DENSITY.sectionGap, sm: 2 }}
      justifyContent="center"
    >
      {showRaTraining && <Grid item xs={12} sm={6} md={desktopColumns} xl={3}>
        <RaTrainingTile onOpen={onOpenRaTraining!} />
      </Grid>}
      {orderedDefinitions.map((def, i) => (
        <WorkflowCard
          key={def.type}
          type={def.type}
          title={def.title}
          stageCount={def.stageCount}
          description={def.description}
          lottie={def.lottie}
          onSelect={onSelect}
          index={i}
          desktopColumns={desktopColumns}
        />
      ))}
    </Grid>
  );
};

export default WorkflowTypeGrid;
